import _ from 'lodash';
import LivePage from '../LivePage';
import LegoAdminPageContext from '../legoAdminPageContext';
import submenuCerokm from '../menus/submenu-cerokm';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import LegoContextSummary from '../../components/lego/LegoContextSummary';
import BadgeId from '../../components/common/BadgeId';
import NiceDate from '../../components/common/NiceDate';
import { DomainIconLink } from './components/SpecSourceBadge';
import { IconButton } from '../../components/common/IconButton';

class CeroKmSourcesManager extends LivePage {
  constructor(props) {
    super(props);
    this.svcSource = this.service('services/cerokm/source');
    this.svcFragments = this.service('services/cerokm/sourcefragment');
    this.svcFragmentBatch = this.service('services/cerokm/fragment-batch');

    this.submenu = submenuCerokm;

    let filters = this.getUrlParam('filters', true);

    // Merge base state with component-specific state
    this.state = {
      ...this.state, // Include base state from LivePage
      sources: null,
      stats: null,
      loading: true,
      filters: filters || { context: { modelId: 'Toyota-Tacoma' } }
    };

    if (filters) {
      console.log("Loaded filters from URL", filters)
    }
  }
  componentDidMount() {
    super.componentDidMount();
    this.refresh();
  }

  async refresh() {
    this.runAsync(async () => {
      this.setState({ loading: true });
      // const filter = { sourceUrl: {$regex: "caranddriver"}};
      const filter = {};
      try {
        const [sourcesRes, statsRes, statsByContext] = await Promise.all([
          this.svcSource.find({ query: { $sort: { name: 1 }, ...filter } }),
          this.svcFragmentBatch.find({ query: { id: 'fragmentsBySource' } }),
          this.svcFragmentBatch.find({ query: { id: 'fragmentsBySourceContext' } })
        ]);
        const statsMap = _.keyBy(statsRes, 'sourceId');

        this.setState({
          sources: sourcesRes,
          stats: statsMap,
          statsByContext: statsByContext,
          loading: false
        });
      } catch (error) {
        console.error("Failed to refresh data:", error);
        this.setState({ loading: false, error: error }); // Optionally handle error state
      }
    });
  }

  async deleteSources(objs) {
    const ids = _.map(objs, '_id');
    const counts = await this.svcFragmentBatch.remove({ query: { sourceIds: ids, dryRun: true } });

    const confirmed = confirm(`Are you REALLY SURE you wanna delete this ${objs.length} sources? It will also delete:\n
      - ${counts.fragmentsToDelete} fragments
      - ${counts.factoids} factoids
      - ${counts.specGroups} spec groups
      
      And remove the sourceIds from ${counts.fragmentsToUpdate} fragments.`);

    if (confirmed) {
      await this.svcFragmentBatch.remove({ query: { sourceIds: ids } });
      this.refresh();
    }
  }

  renderPageBody() {
    const { filters, sources, stats, statsByContext, loading } = this.state;

    if (loading) {
      return <div className={'p-3 text-center position-relative'}>

      </div>;
    }

    if (!sources || sources.length === 0) {
      return <div className={'p-3 text-center'}>No sources found.</div>;
    }

    // Define the grouping levels in order of hierarchy
    // Make sure the keys exist in source.context or handle potential errors
    const groupingLevels = [
      { key: 'context.year', label: 'Year', displayValue: (x) => x },
      { key: 'context.modelId', label: 'Model', displayValue: (x) => <LegoContextSummary inline context={{ modelId: x }} /> },
      { key: 'context.ver', label: 'Version', displayValue: (x) => <LegoContextSummary inline context={{ ver: x }} /> }, // Add or remove levels here
    ];

    // Recursive function to group data and calculate totals
    function groupAndTotal(items, level, groupingLevels, statsMap) {
      // Calculate total for the current list of items (sources)
      let currentTotal = null;


      // Base case: If we've processed all grouping levels, return items and their total
      if (level >= groupingLevels.length) {
        currentTotal = _.find(statsByContext, o => _.isEqual(o.sourceContext, items[0].context))?.count || 0;
        return { items: items, total: currentTotal };
      }

      const currentLevelConf = groupingLevels[level];
      // Group items by the current level's key, using 'N/A' if key is missing
      const grouped = _.groupBy(items, item => _.get(item, currentLevelConf.key, '-'));


      const results = {};
      // Recursively process each subgroup
      for (const groupValue in grouped) {
        const groupItems = grouped[groupValue];
        // Pass the subgroup items to the next level
        results[groupValue] = groupAndTotal(groupItems, level + 1, groupingLevels, statsMap);
      }


      // Base case: If we've processed all grouping levels, return items and their total
      if (level === groupingLevels.length - 1 && _.keys(results).length === 1) {
        currentTotal = _.find(statsByContext, o => _.isEqual(o.sourceContext, items[0].context))?.count || 0;
      }

      // Return the grouped results and the total for this level
      return { groups: results, total: currentTotal };
    }

    // Recursive component to render the grouped rows
    const RenderGroupedRows = ({ data, level, groupingLevels, statsMap }) => {
      // Base case: Render individual sour¶ce rows
      if (data.items) {
        return data.items.map(source => (
          <tr key={source._id}>
            {/* Add indentation to the first cell based on depth */}
            <td className='text-center'>{ source.lastVisited ? <NiceDate t={source.lastVisited} /> : ''}</td>
            <td className="monospace small">
              {source.sourceUrl ? <DomainIconLink url={source.sourceUrl} className="rounded mr-1 text-secondary">
                {source.name || source.sourceUrl}
              </DomainIconLink> : <span className="text-secondary">{source.name || ''}</span>}
              <div className='small text-info'>{JSON.stringify(source.sourceMetadata)}</div>
            </td>
            <td><span className='badge badge-secondary'>{source.sourceType}</span></td>
            <td className='text-right pr-4 monospace text-primary'>{statsMap[source._id]?.count || 0}</td>
            <td><BadgeId id={source._id} /></td>
            <td>
              <IconButton icon='delete' level='danger' onClick={() => this.runAsync(this.deleteSources([source]))} />
            </td>
          </tr>
        ));
      }

      // Recursive case: Render group headers and children
      const rows = [];

      // Sort group keys for consistent order (optional, but good practice)
      const sortedGroupKeys = Object.keys(data.groups).sort();

      sortedGroupKeys.forEach(groupValue => {
        const groupData = data.groups[groupValue];
        const displayValue = groupingLevels[level].displayValue || (x => x.toString());
        // Group Header Row
        // if (groupValue !== '-' || _.keys(data.groups).length > 1) {
        if (true) {
          rows.push(
            <tr key={`${level}-${groupValue}-header`} style={{ backgroundColor: `rgba(0,0,0,${0.05 + level * 0.03})` }}>
              {/* Indent header based on level */}
              <td colSpan="3" style={{ paddingLeft: (level + 1) * 25 + 'px' }}>
                <strong>{displayValue(groupValue)}</strong>
              </td>
              {/* Show total fragment count for this group */}
              <td colSpan={2} className='text-right pr-4 monospace text-primary'>
                {groupData.total > 1 && <span className='text-secondary small mr-1'> (unique) </span>}
                <strong>{groupData.total}</strong>
              </td>
              <td>
                <IconButton icon='delete' level='danger' onClick={() => this.runAsync(this.deleteSources(groupData.items))} />
              </td>
            </tr>
          );
        }

        // Recursively render children rows
        rows.push(
          <RenderGroupedRows
            key={`${level}-${groupValue}-children`}
            data={groupData}
            level={level + 1} // Increment level for children
            groupingLevels={groupingLevels}
            statsMap={statsMap} // Pass statsMap down
          />
        );
      });

      return rows;
    }

    // Group the sources using the defined levels and calculate totals
    const groupedData = groupAndTotal(sources, 0, groupingLevels, stats);

    return <div className={'px-3 py-2'}>
      <table className="table table-bordered table-sm">
        <thead>
          <tr>
            <th>Last Visited</th>
            <th>Source Name / URL</th>
            <th>Type</th>
            <th>Fragment Count</th>
            <th>Source ID</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr className='bg-highlight' style={{ fontWeight: 'bold' }}>
            <td colSpan="3">TOTAL</td>
            <td className='text-right pr-4 monospace text-primary'><strong>{groupedData.total}</strong></td>
            <td colSpan="2"></td>
          </tr>

          <RenderGroupedRows
            data={groupedData}
            level={0}
            groupingLevels={groupingLevels}
            statsMap={stats}
          />
        </tbody>
      </table>
    </div>;
  }
}

CeroKmSourcesManager.contextType = LegoAdminPageContext;

// For development
function FuncComponent(props) {
  return <CeroKmSourcesManager {...props} />;
}

export default FuncComponent;
